import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  DollarSign,
  AlertTriangle,
  Clock,
  Search,
  Loader2,
  UserPlus,
} from "lucide-react";

import adminService from "@/services/adminService";

const SEVERITY_COLORS = {
  INFO: "bg-blue-500/20 text-blue-300",
  WARN: "bg-yellow-500/20 text-yellow-300",
  CRITICAL: "bg-red-500/20 text-red-300",
  MEDIUM: "bg-yellow-500/20 text-yellow-300",
  HIGH: "bg-red-500/20 text-red-300",
  LOW: "bg-blue-500/20 text-blue-300",
};

export default function AdminQueues() {
  const [loading, setLoading] = useState(true);
  const [queues, setQueues] = useState(null);
  const [activeTab, setActiveTab] = useState("approvals");
  const [searchQuery, setSearchQuery] = useState("");

  // Action modals
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [reason, setReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [suggestedNarrators, setSuggestedNarrators] = useState([]);
  const [selectedNarrator, setSelectedNarrator] = useState(null);

  useEffect(() => {
    loadQueues();
  }, []);

  const loadQueues = async () => {
    try {
      setLoading(true);
      const data = await adminService.getQueues();
      setQueues(data);
    } catch (error) {
      console.error("Error loading queues:", error);
      toast.error("Failed to load queues");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (item) => {
    try {
      setProcessing(true);
      if (item.type === "SUBMISSION") {
        await adminService.approveSubmission(item.entity_id);
        toast.success("Submission approved");
      } else if (item.type === "DEMO") {
        await adminService.reviewDemo(item.entity_id, {
          status: "APPROVED",
          is_public: true,
        });
        toast.success("Demo approved");
      }
      await loadQueues();
    } catch (error) {
      console.error("Error approving:", error);
      toast.error("Failed to approve");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!reason || reason.length < 5) {
      toast.error("Please provide a reason (min 5 characters)");
      return;
    }

    try {
      setProcessing(true);
      if (selectedItem.type === "SUBMISSION") {
        await adminService.rejectSubmission(selectedItem.entity_id, reason);
        toast.success("Submission rejected");
      } else if (selectedItem.type === "DEMO") {
        await adminService.reviewDemo(selectedItem.entity_id, {
          status: "REJECTED",
          reviewer_notes: reason,
        });
        toast.success("Demo rejected");
      }
      setShowRejectModal(false);
      setReason("");
      await loadQueues();
    } catch (error) {
      console.error("Error rejecting:", error);
      toast.error("Failed to reject");
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenActivate = async (item) => {
    setSelectedItem(item);
    setShowActivateModal(true);
    setReason("");
    setSelectedNarrator(null);

    // Load suggested narrators
    try {
      if (item.entity_type === "BOOK") {
        const data = await adminService.getSuggestedNarrators(item.entity_id);
        setSuggestedNarrators(data.suggested_narrators || []);
      }
    } catch (error) {
      console.error("Error loading narrators:", error);
    }
  };

  const handleActivateFunding = async () => {
    if (!reason || reason.length < 5) {
      toast.error("Please provide a reason (min 5 characters)");
      return;
    }

    if (!selectedNarrator) {
      toast.error("Please select a narrator");
      return;
    }

    try {
      setProcessing(true);
      await adminService.activateFunding(
        selectedItem.entity_type,
        selectedItem.entity_id,
        {
          narrator_id: selectedNarrator.id,
          reason,
          auto_assign: false,
        }
      );
      toast.success("Funding activated and narrator assigned!");
      setShowActivateModal(false);
      await loadQueues();
    } catch (error) {
      console.error("Error activating:", error);
      toast.error(error.response?.data?.detail || "Failed to activate");
    } finally {
      setProcessing(false);
    }
  };

  const handleResolveQC = async (item) => {
    try {
      setProcessing(true);
      await adminService.resolveQCIssue(item.entity_id);
      toast.success("QC issue resolved");
      await loadQueues();
    } catch (error) {
      console.error("Error resolving:", error);
      toast.error("Failed to resolve");
    } finally {
      setProcessing(false);
    }
  };

  const filterItems = (items) => {
    if (!searchQuery) return items;
    return items.filter(
      (item) =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.entity_id?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-queues">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Work Queues</h1>
          <p className="text-slate-400">Manage pending tasks and approvals</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="pl-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="approvals" className="text-white data-[state=active]:text-white data-[state=active]:bg-slate-700 data-[state=active]:border-slate-500"
>
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Approvals ({queues?.approvals?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="funding" className="text-white data-[state=active]:text-white data-[state=active]:bg-slate-700 data-[state=active]:border-slate-500"
>
            <DollarSign className="w-4 h-4 mr-2" />
            Funding ({queues?.funding_activation?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="qc" className="text-white data-[state=active]:text-white data-[state=active]:bg-slate-700 data-[state=active]:border-slate-500"
>
            <AlertTriangle className="w-4 h-4 mr-2" />
            QC Issues ({queues?.qc_issues?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="overdue" className="text-white data-[state=active]:text-white data-[state=active]:bg-slate-700 data-[state=active]:border-slate-500"
>
            <Clock className="w-4 h-4 mr-2" />
            Overdue ({queues?.overdue?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Approvals Tab */}
        <TabsContent value="approvals" className="space-y-3 mt-4">
          {filterItems(queues?.approvals || []).map((item) => (
            <Card key={item.id} className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={SEVERITY_COLORS[item.severity]}>
                        {item.type}
                      </Badge>
                      <span className="text-white font-medium">{item.title}</span>
                    </div>
                    <p className="text-sm text-slate-400">
                      {item.metadata?.language && `Language: ${item.metadata.language}`}
                      {item.metadata?.narrator_name && ` • ${item.metadata.narrator_name}`}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(item)}
                      disabled={processing}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedItem(item);
                        setShowRejectModal(true);
                      }}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {queues?.approvals?.length === 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center text-slate-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No pending approvals</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Funding Tab */}
        <TabsContent value="funding" className="space-y-3 mt-4">
          {filterItems(queues?.funding_activation || []).map((item) => (
            <Card key={item.id} className="bg-slate-800/50 border-slate-700 border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-green-500/20 text-green-300">
                        {item.metadata?.contributed_pct?.toFixed(0)}% Funded
                      </Badge>
                      <span className="text-white font-medium">{item.title}</span>
                    </div>
                    <p className="text-sm text-slate-400">
                      {item.entity_type} • {item.metadata?.currency || "USD"}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleOpenActivate(item)}
                    className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Activate & Assign
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {queues?.funding_activation?.length === 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center text-slate-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No funding activations pending</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* QC Tab */}
        <TabsContent value="qc" className="space-y-3 mt-4">
          {filterItems(queues?.qc_issues || []).map((item) => (
            <Card key={item.id} className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={SEVERITY_COLORS[item.severity]}>
                        {item.severity}
                      </Badge>
                      <Badge variant="outline" className="text-slate-400">
                        {item.metadata?.issue_type}
                      </Badge>
                    </div>
                    <p className="text-white">{item.title}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleResolveQC(item)}
                    disabled={processing}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Resolve
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {queues?.qc_issues?.length === 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center text-slate-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No open QC issues</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Overdue Tab */}
        <TabsContent value="overdue" className="space-y-3 mt-4">
          {filterItems(queues?.overdue || []).map((item) => (
            <Card key={item.id} className="bg-slate-800/50 border-slate-700 border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-red-500/20 text-red-300">OVERDUE</Badge>
                      <span className="text-white font-medium">{item.title}</span>
                    </div>
                    <p className="text-sm text-slate-400">
                      Due: {item.metadata?.due_date} • Status: {item.status}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {queues?.overdue?.length === 0 && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No overdue projects</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Reject {selectedItem?.type}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Please provide a reason for rejection (required)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for rejection..."
              className="bg-slate-700 border-slate-600 text-white"
              rows={4}
            />
            <div className="flex gap-3">
              <Button
                onClick={handleReject}
                disabled={processing || reason.length < 5}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Reject
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowRejectModal(false)}
                className="flex-1 border-slate-600"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activate Funding Modal */}
      <Dialog open={showActivateModal} onOpenChange={setShowActivateModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Activate Funding & Assign Narrator</DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedItem?.title} • {selectedItem?.metadata?.contributed_pct?.toFixed(0)}% funded
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                Select Narrator
              </label>
              <div className="space-y-2 max-h-48 overflow-auto">
                {suggestedNarrators.map((narrator) => (
                  <div
                    key={narrator.id}
                    onClick={() => setSelectedNarrator(narrator)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedNarrator?.id === narrator.id
                        ? "border-purple-500 bg-purple-500/20"
                        : "border-slate-600 hover:border-slate-500"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{narrator.display_name}</p>
                        <p className="text-xs text-slate-400">
                          {narrator.languages?.join(", ")} • {narrator.availability_status}
                        </p>
                      </div>
                      {narrator.is_verified && (
                        <Badge className="bg-green-500/20 text-green-300 text-xs">
                          Verified
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
                {suggestedNarrators.length === 0 && (
                  <p className="text-slate-500 text-center py-4">
                    No matching narrators found
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                Reason (required)
              </label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for activation..."
                className="bg-slate-700 border-slate-600 text-white"
                rows={3}
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleActivateFunding}
                disabled={processing || reason.length < 5 || !selectedNarrator}
                className="flex-1 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Activate & Assign
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowActivateModal(false)}
                className="flex-1 border-slate-600"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
